import {
  Clear as ClearIcon,
  FilterAlt as FilterIcon,
  Search as SearchIcon,
  Sort as SortIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import React from "react";
import { ActivityFilters as Filter } from "../../../types/ActivityList.types";

interface ActivityFiltersProps {
  filters: Filter;
  onFiltersChange: (filters: Filter) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
}

const ActivityFilters: React.FC<ActivityFiltersProps> = ({
  filters,
  onFiltersChange,
  showFilters,
  onToggleFilters,
}) => {
  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value });
  };

  const handleDateFromChange = (date: Date | null) => {
    onFiltersChange({
      ...filters,
      dateFilter: { ...filters.dateFilter, from: date },
    });
  };

  const handleDateToChange = (date: Date | null) => {
    onFiltersChange({
      ...filters,
      dateFilter: { ...filters.dateFilter, to: date },
    });
  };

  const clearDateFilters = () => {
    onFiltersChange({
      ...filters,
      dateFilter: { from: null, to: null },
      showAll: false,
      search: "",
    });
  };

  const hasActiveFilters =
    filters.dateFilter.from || filters.dateFilter.to || filters.search;

  return (
    <Box>
      <Box sx={{ display: "flex", mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Sök aktiviteter..."
          value={filters.search}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: filters.search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleSearchChange("")}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          size="small"
          sx={{ mr: 1 }}
        />

        <Tooltip title="Filtrera">
          <IconButton
            onClick={onToggleFilters}
            color={hasActiveFilters ? "primary" : "default"}
          >
            <FilterIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Sortera">
          <IconButton>
            <SortIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={showFilters}>
        <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <DatePicker
              label="Från datum"
              value={filters.dateFilter.from}
              onChange={handleDateFromChange}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 },
                },
              }}
            />

            <DatePicker
              label="Till datum"
              value={filters.dateFilter.to}
              onChange={handleDateToChange}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 },
                },
              }}
            />
            <Checkbox
              checked={filters.showAll}
              onChange={(e) =>
                onFiltersChange({ ...filters, showAll: e.target.checked })
              }
            />
            <span>Visa avslutade</span>

            {/* Clear filters button */}
            {hasActiveFilters && (
              <Button
                startIcon={<ClearIcon />}
                onClick={clearDateFilters}
                size="small"
                variant="outlined"
              >
                Rensa filter
              </Button>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ActivityFilters;
